/**
 * Simple HTML Generator for StorySplat
 *
 * Generates standalone HTML that loads the viewer from CDN.
 * This replaces the old 13K+ line HTML generation system with a simple
 * bootstrap HTML that uses the same viewer code as dynamic embedding.
 */

import type { SceneData } from '../types';

declare const __PACKAGE_VERSION__: string;
const PACKAGE_VERSION: string = typeof __PACKAGE_VERSION__ !== 'undefined' ? __PACKAGE_VERSION__ : 'latest';

declare const __PLAYCANVAS_VERSION__: string;
const PLAYCANVAS_VERSION: string = typeof __PLAYCANVAS_VERSION__ !== 'undefined' ? __PLAYCANVAS_VERSION__ : '2.23.2';

export interface GenerateHTMLOptions {
  /** Custom CDN URL for the viewer bundle. Default: unpkg */
  cdnUrl?: string;
  /** HTML page title. Default: scene name */
  title?: string;
  /** Meta description for SEO */
  description?: string;
  /** Favicon URL */
  faviconUrl?: string;
  /** Custom CSS to inject */
  customCSS?: string;
  /** Whether to minify the output */
  minify?: boolean;
  /** Enable lazy loading - shows thumbnail with start button before loading (overrides scene data) */
  lazyLoad?: boolean;
  /** Custom button text for lazy loading (overrides scene data) */
  lazyLoadButtonText?: string;
  /** Analytics configuration for tracking sessions and interactions */
  analytics?: {
    sceneId: string;
    ownerId: string;
    baseUrl?: string;
  };
  /** Short-lived hosted asset lease context supplied only by the trusted scene route. */
  assetLease?: {
    sceneId: string;
    ownerId: string;
    expiresAtMs: number;
    renewalEndpoint?: string;
  };
  /** Version-independent lease shim loaded before a historical pinned bundle. */
  assetLeaseRuntimeUrl?: string;
  /** Disable StorySplat and Google Analytics tracking */
  disableAnalytics?: boolean;
  /** Google Analytics Measurement ID, for example G-XXXXXXXXXX */
  googleTagId?: string;
  /**
   * Optional hosted-scene route prefix used for portal navigation. When set,
   * portal targets reload through this route so each target can select its own
   * viewer version instead of reusing the current document's runtime.
   */
  portalNavigationBaseUrl?: string;
  /** Validated query suffix (including leading ?) to preserve hosted options across portals. */
  portalNavigationQuery?: string;
  /**
   * The exact viewer version this generated document runs (the bundle referenced
   * by `cdnUrl`). Passed to the viewer so a portal teleport reloads ONLY when the
   * target pins a different version; a same-version chain (or a floating runtime,
   * when this is omitted) swaps in place and keeps the cross-scene audio bed.
   */
  portalNavigationRuntimeVersion?: string | null;
  /**
   * Metadata shim for historical bundled releases whose build left the
   * compile-time version globals unresolved. New builds bake literals and
   * simply ignore these globals.
   */
  viewerVersionMetadata?: {
    packageVersion: string;
    buildLabel?: string;
  };
}

/**
 * Escape HTML entities in a string
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Escape string for safe embedding in inline <script> tags.
 * Prevents XSS by escaping </script> sequences that would close the script tag prematurely.
 */
function escapeForInlineScript(str: string): string {
  // Escape </script> (case-insensitive) to prevent script tag injection
  // Using Unicode escapes for the < character within script context
  return str.replace(/<\/script/gi, '<\\/script');
}

function serializeForInlineScript(value: unknown): string {
  const json = JSON.stringify(value);
  if (json === undefined) return 'undefined';
  return escapeForInlineScript(json)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

const GOOGLE_TAG_ID_PATTERN = /^G-[A-Z0-9]{6,20}$/i;

function normalizeGoogleTagId(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return GOOGLE_TAG_ID_PATTERN.test(trimmed) ? trimmed.toUpperCase() : undefined;
}

/**
 * Generate standalone HTML for a StorySplat scene
 *
 * @param sceneData - Scene data object (same format as createViewer)
 * @param options - Generation options
 * @returns Complete HTML string ready to be saved as .html file
 *
 * @example
 * ```typescript
 * import { generateHTML } from 'storysplat-viewer';
 *
 * const html = generateHTML(sceneData, { title: 'My Scene' });
 * // Save html to file or serve it
 * ```
 */
export function generateHTML(sceneData: SceneData, options: GenerateHTMLOptions = {}): string {
  const {
    cdnUrl = `https://unpkg.com/storysplat-viewer@${PACKAGE_VERSION}/dist/storysplat-viewer.umd.js`,
    title = sceneData.name || 'StorySplat Scene',
    description = `Interactive 3D scene: ${sceneData.name || 'StorySplat'}`,
    faviconUrl,
    customCSS = '',
    lazyLoad: optionsLazyLoad,
    lazyLoadButtonText: optionsLazyLoadButtonText,
    analytics,
    assetLease,
    assetLeaseRuntimeUrl,
    disableAnalytics = false,
    googleTagId,
    portalNavigationBaseUrl,
    portalNavigationQuery = '',
    portalNavigationRuntimeVersion,
    viewerVersionMetadata,
  } = options;

  // Resolve lazy load settings (options override scene data)
  const lazyLoad = optionsLazyLoad ?? sceneData.uiOptions?.lazyLoad ?? false;
  const lazyLoadButtonText = optionsLazyLoadButtonText ?? sceneData.uiOptions?.lazyLoadButtonText;
  // Generated HTML is downloadable/self-hostable. Restrict *all* StorySplat
  // telemetry to StorySplat hosts so a saved copy opened on a customer domain
  // or file:// never creates a visitor id or phones home by default.
  const analyticsOptions = analytics && !disableAnalytics
    ? {
        ...analytics,
        onlyReportAnalyticsOnStorySplatHost: true,
        // Retain the older flag for runtimes that only understand the
        // bandwidth-specific guard.
        onlyReportBandwidthOnStorySplatHost: true,
      }
    : undefined;
  const safeGoogleTagId = !disableAnalytics ? normalizeGoogleTagId(googleTagId) : undefined;

  // PlayCanvas CDN URL - loaded before viewer bundle (UMD expects 'pc' global).
  // Baked at build time from the engine version installed when this viewer was
  // compiled, so standalone exports always run the exact engine the bundle was
  // built and tested against. (A hardcoded pin here previously drifted: it said
  // 2.18 while the viewer compiled against 2.20.) The literal fallback only
  // applies when the source is consumed without the rollup define.
  const playcanvasCdnUrl = `https://cdn.jsdelivr.net/npm/playcanvas@${PLAYCANVAS_VERSION}/build/playcanvas.min.js`;

  const faviconTag = faviconUrl
    ? `<link rel="icon" href="${escapeHtml(faviconUrl)}" />`
    : '';

  const customCSSBlock = customCSS
    ? `<style>${customCSS}</style>`
    : '';

  // Serialize scene data, handling circular references
  // Then escape for safe embedding in inline <script> tags
  const rawJSON = JSON.stringify(sceneData, (key, value) => {
    // Skip any DOM nodes or circular refs
    // Check for HTMLElement safely (it doesn't exist in Node.js)
    if (typeof HTMLElement !== 'undefined' && value instanceof HTMLElement) return undefined;
    if (typeof value === 'function') return undefined;
    // Skip any object with nodeType (DOM nodes)
    if (value && typeof value === 'object' && 'nodeType' in value) return undefined;
    return value;
  }, 2);
  // Escape </script> sequences to prevent XSS attacks
  const sceneDataJSON = escapeForInlineScript(rawJSON);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
  <meta name="description" content="${escapeHtml(description)}">
  <title>${escapeHtml(title)}</title>
  ${faviconTag}
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body {
      width: 100%;
      /* Use 100dvh for iOS address bar support, with 100vh fallback */
      height: 100vh;
      height: 100dvh;
      overflow: hidden;
      background: #111;
      touch-action: none;
      -webkit-touch-callout: none;
    }
    #app {
      width: 100%;
      /* Use 100dvh for iOS address bar support, with 100vh fallback */
      height: 100vh;
      height: 100dvh;
      position: relative;
    }
    /* Loading state */
    #app:empty::after {
      content: 'Loading...';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: #666;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 16px;
    }
  </style>
  ${customCSSBlock}
</head>
<body>
  <div id="app"></div>

  <script src="${playcanvasCdnUrl}"></script>
  ${viewerVersionMetadata ? `<script>
    globalThis.__PACKAGE_VERSION__ = ${serializeForInlineScript(viewerVersionMetadata.packageVersion)};
    globalThis.__BUILD_TIMESTAMP__ = ${serializeForInlineScript(viewerVersionMetadata.buildLabel || `pinned npm v${viewerVersionMetadata.packageVersion}`)};
  </script>` : ''}
  ${assetLeaseRuntimeUrl ? `<script src="${escapeHtml(assetLeaseRuntimeUrl)}"></script>` : ''}
  <script src="${escapeHtml(cdnUrl)}"></script>
  <script>
    (function() {
      'use strict';

      var sceneData = ${sceneDataJSON};

      // Wait for DOM and viewer to be ready
      async function init() {
        var container = document.getElementById('app');
        if (!container) {
          console.error('[StorySplat] Container #app not found');
          return;
        }

        if (typeof StorySplatViewer === 'undefined' || !StorySplatViewer.createViewer) {
          console.error('[StorySplat] Viewer not loaded. Check CDN URL.');
          return;
        }

        try {
          var assetLeaseHandle = null;
          ${assetLease && assetLeaseRuntimeUrl ? `if (window.StorySplatAssetLease && typeof window.StorySplatAssetLease.createAssetLeaseManager === 'function') {
            assetLeaseHandle = window.StorySplatAssetLease.createAssetLeaseManager(${serializeForInlineScript(assetLease)});
            window.addEventListener('pagehide', function() { if (assetLeaseHandle) assetLeaseHandle.dispose(); }, { once: true });
          }` : ''}
          // createViewer is async (awaits WebGPU device creation, falls back
          // to WebGL2/WebGL1). Returns a Promise<ViewerInstance>.
          var viewer = await StorySplatViewer.createViewer(container, sceneData, {
            showUI: true,
            autoPlay: false,
            lazyLoad: ${lazyLoad},
            lazyLoadButtonText: ${lazyLoadButtonText ? serializeForInlineScript(lazyLoadButtonText) : 'undefined'}${analyticsOptions ? `,
            analytics: ${serializeForInlineScript(analyticsOptions)}` : ''}${assetLease && !assetLeaseRuntimeUrl ? `,
            assetLease: ${serializeForInlineScript(assetLease)}` : ''}${disableAnalytics ? `,
            disableAnalytics: true` : ''}${safeGoogleTagId ? `,
            googleTagId: ${serializeForInlineScript(safeGoogleTagId)}` : ''}${portalNavigationBaseUrl ? `,
            portalNavigation: { baseUrl: ${serializeForInlineScript(portalNavigationBaseUrl)}, query: ${serializeForInlineScript(portalNavigationQuery)}${portalNavigationRuntimeVersion ? `, runtimeVersion: ${serializeForInlineScript(portalNavigationRuntimeVersion)}` : ''} }` : ''}
          });

          viewer.on('ready', function() {
            console.log('[StorySplat] Scene ready');
          });

          viewer.on('error', function(err) {
            console.error('[StorySplat] Error:', err);
          });

          // Portal teleports are handled inside the viewer via the portalNavigation
          // option above: an IN-PLACE scene swap (keeps the cross-scene ambient audio
          // bed alive) + history.pushState for deep-links/back button, with a full
          // reload only for version-pinned targets. (Previously a portalActivated
          // handler here hard-navigated on every teleport, which cut the audio.)

          // Expose viewer globally for debugging
          window.storySplatViewer = viewer;
        } catch (err) {
          console.error('[StorySplat] Failed to create viewer:', err);
        }
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
      } else {
        init();
      }
    })();
  </script>
</body>
</html>`;
}

/**
 * Generate HTML from a scene URL (fetches the JSON first)
 *
 * @param jsonUrl - URL to fetch scene JSON from
 * @param options - Generation options
 * @returns Complete HTML string
 */
export async function generateHTMLFromUrl(
  jsonUrl: string,
  options: GenerateHTMLOptions = {}
): Promise<string> {
  const response = await fetch(jsonUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch scene: ${response.statusText}`);
  }
  const sceneData: SceneData = await response.json();
  return generateHTML(sceneData, options);
}
